import React, { useEffect, useState } from 'react';
import { actions, containers } from '@ecosystem/newron-design-system';
import { Context, Entity, FilterSections, FiltersValues } from '../types';
import { ContextManager } from '../context/manager';
import { FilterSection } from './filterSection';
import { Loading } from '../design/loading/Loading';
import { ClientDrawer, FilterPanel } from '@ecosystem/newron-client-lib';
import { useFilterTracking } from './filterTracking.hook';
import { getError, pickFeatureFilters } from './utils';
import { SavedFilters } from './savedFilters';
import { useSavedFiltersContext } from './savedFilters.provider';
import { FilterTrayTabId as FilterTrayTabIdEnum } from '@ecosystem/newron-design-system/build/exports/containers';
import { useMobile } from '../context/mobile/mobile.provider';

const { Button } = actions;
const { FilterTray, FilterTrayTabId } = containers;

function FiltersPanel() {
  const [context, setContext] = useState<Context>();
  const { isMobile } = useMobile();
  useEffect(() => {
    const subscriber = (context: Context) => {
      if (context) {
        setContext(context);
      }
    };
    ContextManager.getInstance().subscribeToSettingsChanges(subscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(subscriber);
  }, []);

  if (!context?.filters) {
    return (
      <FilterPanel>
        <Loading isActive withIndicator />
      </FilterPanel>
    );
  }

  const {
    filters: { sections, values },
  } = context;
  if (!sections?.length) {
    return null;
  }
  if (isMobile) {
    sections.map(section => {
      if (section.label === 'Market') {
        if (section.ungrouped) {
          section.ungrouped.definitions.map(def => {
            if (def.name === 'brand_name') {
              def.label = 'Brands / brand owners';
            }
            return def;
          });
        }
      }
      return section;
    });
  }

  return <FiltersPanelComponent sections={sections} values={values} />;
}

type ComponentProps = {
  sections: FilterSections[];
  values: FiltersValues;
};

function FiltersPanelComponent({ sections, values }: ComponentProps) {
  const [filterValues, setFilterValues] = useState<FiltersValues>(values);
  const [activeTab, setActiveTab] = useState<FilterTrayTabIdEnum>(FilterTrayTabId.Configure);
  const { trackFilterApplied } = useFilterTracking(sections);
  const { saveFilterEnabled, newFilterId } = useSavedFiltersContext();
  const { openDrawer, closeDrawer, expanded } = ClientDrawer.useClientDrawer();
  const selectedFeatureFilters = pickFeatureFilters(filterValues, sections);
  const showError = getError(selectedFeatureFilters);

  useEffect(
    function handleMarketChanged() {
      setFilterValues(values);
    },
    [sections, values]
  );

  useEffect(() => {
    if (newFilterId) {
      openDrawer();
      setActiveTab(FilterTrayTabId.SavedFilters);
    }
  }, [newFilterId, openDrawer]);

  useEffect(() => {
    // other MFEs can hide the drawer by setting --right-sidebar-width to 0 (racing condition?),
    // so we have to restore the width if the drawer is expanded
    if (!expanded) {
      return;
    }
    let fixTraySizeTimeout;
    fixTraySizeTimeout = setTimeout(() => {
      if (document.documentElement.style.getPropertyValue('--right-sidebar-width') === '0px') {
        closeDrawer();
        openDrawer();
      }
    }, 100);

    return () => {
      clearTimeout(fixTraySizeTimeout);
    };
  }, [expanded, closeDrawer, openDrawer]);

  useEffect(
    function handleClearAllClicked() {
      if (Object.keys(values).length === 0) {
        setFilterValues({});
      }
    },
    [values]
  );

  const handleClearAll = () => {
    setFilterValues({});
    ContextManager.getInstance().clearFilterValues();
  };

  const handleApply = () => {
    ContextManager.getInstance().updateFilterValues(filterValues);
    trackFilterApplied(filterValues);
  };

  const handleChange = (name: string, values: Entity[]) => {
    setFilterValues(prevFilters => ({ ...prevFilters, [name]: values }));
  };

  const handleIsDisabled =
    ({ label, isMain }: FilterSections) =>
    (definitionName: string) => {
      return (
        showError && Object.keys(!selectedFeatureFilters[label].selectedFilters).includes(definitionName) && !isMain
      );
    };

  const hasSections = !!sections?.length;

  return (
    <FilterPanel>
      <FilterTray.Content onTabChange={setActiveTab} activeTab={activeTab}>
        <FilterTray.Configure>
          {hasSections &&
            sections.map(section => (
              <FilterSection
                key={section.label}
                section={section}
                values={filterValues}
                onChange={handleChange}
                getIsDisabled={handleIsDisabled(section)}
              />
            ))}
          {!hasSections && (
            <span className="block text-center text-sm text-lighter px-8 pt-20">No filters are available!</span>
          )}
        </FilterTray.Configure>
        {saveFilterEnabled && (
          <FilterTray.SavedFilters>
            <SavedFilters />
          </FilterTray.SavedFilters>
        )}
      </FilterTray.Content>

      <FilterTray.Footer errorMessage={showError}>
        {activeTab !== FilterTrayTabId.SavedFilters && (
          <div className="flex">
            <Button mode="secondary" onClick={handleClearAll} stretch>
              Clear
            </Button>
            <Button mode="primary" onClick={handleApply} disabled={!!showError} stretch>
              Apply
            </Button>
          </div>
        )}
      </FilterTray.Footer>
    </FilterPanel>
  );
}

export { FiltersPanel, FiltersPanelComponent };
